import type {MilestonePickerMilestone} from '@github-ui/item-picker-milestone'
import type {SetFieldsOperation} from '@github-ui/optimistic-connection'
import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import {updateIssueMilestoneRelayMutation as updateIssueMilestoneQueryId} from '@github-ui/server-persisted-queries'
import type {UpdateIssueMilestoneRelayMutationMutation} from '@github-ui/server-persisted-queries/types/updateIssueMilestoneRelayMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

type UpdateIssueMilestoneCallbacks = {
  onError?: (error: Error) => void
  onCompleted?: (response: UpdateIssueMilestoneRelayMutationMutation) => void
}

export type UpdateIssueMilestone = (
  issueId: string,
  milestone: MilestonePickerMilestone | null,
  callbacks?: UpdateIssueMilestoneCallbacks,
) => void

function milestoneOperation(issueId: string, milestone: MilestonePickerMilestone | null): SetFieldsOperation {
  return {type: 'setFields', id: issueId, fields: {milestone}}
}

async function updateIssueMilestoneRequest({
  owner,
  repo,
  issueNumber,
  milestoneId,
  clientMutationId,
}: {
  owner: string
  repo: string
  issueNumber: number
  milestoneId: string | null
  clientMutationId?: string
}): Promise<UpdateIssueMilestoneRelayMutationMutation> {
  const query = new URLSearchParams({queryId: updateIssueMilestoneQueryId})
  const response = await reactFetchJSON(
    `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/issues/${issueNumber}/milestone.json?${query}`,
    {method: 'PATCH', body: {milestoneId, clientMutationId}},
  )

  let result:
    | {data?: UpdateIssueMilestoneRelayMutationMutation | null; errors?: ReadonlyArray<{message?: string}> | null}
    | undefined
  try {
    result = (await response.json()) as typeof result
  } catch {
    result = undefined
  }

  const error = result?.errors?.find(item => item.message)?.message
  const data = result?.data
  const issue = data?.updateIssue?.issue
  const confirmedMilestone = issue?.milestone
  const assignmentMismatch = milestoneId ? confirmedMilestone?.id !== milestoneId : confirmedMilestone !== null
  if (!response.ok || error || !data || !issue || assignmentMismatch) {
    throw new Error(error ?? 'Failed to update issue milestone')
  }
  return data
}

async function notifyMutationResult(
  mutation: Promise<UpdateIssueMilestoneRelayMutationMutation>,
  callbacks?: UpdateIssueMilestoneCallbacks,
): Promise<void> {
  let response: UpdateIssueMilestoneRelayMutationMutation
  try {
    response = await mutation
  } catch (error) {
    callbacks?.onError?.(error instanceof Error ? error : new Error('Failed to update issue milestone'))
    return
  }
  callbacks?.onCompleted?.(response)
}

export function useUpdateIssueMilestoneMutation({
  owner,
  repo,
  issueNumber,
  clientMutationId,
}: {
  owner: string
  repo: string
  issueNumber: number
  clientMutationId?: string
}): UpdateIssueMilestone {
  const {mutateAsync} = useEntityMutation<UpdateIssueMilestoneRelayMutationMutation>({
    scope: {id: `update-issue-milestone:${owner}/${repo}#${issueNumber}`},
    mutationFn: operation => {
      const milestone = operation.fields.milestone as MilestonePickerMilestone | null
      return updateIssueMilestoneRequest({
        owner,
        repo,
        issueNumber,
        milestoneId: milestone?.id ?? null,
        clientMutationId,
      })
    },
    getConfirmedOperation: data => {
      const issue = data.updateIssue?.issue
      return issue ? milestoneOperation(issue.id, issue.milestone) : undefined
    },
  })

  return useCallback(
    (issueId, milestone, callbacks) => {
      void notifyMutationResult(mutateAsync({operation: milestoneOperation(issueId, milestone)}), callbacks)
    },
    [mutateAsync],
  )
}
